import { useLazyQuery } from '@apollo/react-hooks';
import { FormikProvider, useFormik } from 'formik';
import { Form, Formik } from 'formik';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DateRangePicker from '~components/date-range-picker';
import { AvailableTenancyOptionUnit } from '~components/datepicker/tenancy-option-helpers';
import { Select } from '~components/form';
import Typography from '~components/typography';
import {
  BathroomArrangementOptions,
  KitchenArrangementOptions,
  RoomCategoryOptions,
  TenancyLengthOptions,
} from '~graphql/__generated__/globalTypes';
import { TenancyTypeOption } from '~graphql/__generated__/globalTypes';
import {
  SP_QueryAvaiableEndDateForTenancyOptions,
  SP_QueryAvaiableEndDateForTenancyOptionsVariables,
} from '~graphql/__generated__/SP_QueryAvaiableEndDateForTenancyOptions';
import { QUERY_AVAIABLE_END_DATE_FOR_TENANCY_OPTIONS } from '~graphql/select-bed';
import { useTranslation } from '~i18n';
import {
  IconBathroom,
  IconDatepicker,
  IconInfo,
  IconKitchen,
  IconRight,
  IconRoom,
} from '~svg-components';
import styles from './filter-selector-form.module.scss';
import TenancyOptionSelect from './tenancy-option-select';

export interface IDate {
  startDate: any;
  endDate: any;
}
interface Option {
  label: string;
  value: string;
}
interface RoomTypeProps {
  id: string | null;
  originId: number | null;
  name: any;
  kitchenArrangement: KitchenArrangementOptions | null;
  bathroomArrangement: BathroomArrangementOptions | null;
  category: RoomCategoryOptions;
  hasPrice: boolean | null;
}

interface IFilterSelectorProps {
  roomTypes: (RoomTypeProps | null)[] | null;
  roomTypeId: string;
  propertyId: string;
  tenancyOptionId: string;
  tenancyDate: IDate;
  availableTenancyOptions?: AvailableTenancyOptionUnit[] | null;
  tenancyOptionLoading?: boolean;
  setRoomTypeId: (value: string) => void;
  setAcademicYearId: (value: string) => void;
  setTenancyDate: (value: IDate) => void;
  setTenancyOptionId: (value: string) => void;
  setTenancyOptionRateType: (value: string) => void;
}

const FilterSelectorForm: React.FC<IFilterSelectorProps> = ({
  roomTypes,
  roomTypeId,
  propertyId,
  tenancyOptionId,
  tenancyDate,
  availableTenancyOptions,
  tenancyOptionLoading,
  setRoomTypeId,
  setAcademicYearId,
  setTenancyOptionId,
  setTenancyOptionRateType,
  setTenancyDate,
}) => {
  const { t } = useTranslation('student_portal');
  const [tenancyOptionLengthType, setTenancyOptionLengthType] = useState('');
  const [selectTenancyType, setSelectTenancyType] = useState<TenancyTypeOption | null>();
  const [flexibleTenancyInfo, setFlexibleTenancyInfo] = useState<{
    tenancyOptionId: string;
    startDate: string;
    endDate: string;
    selectableStartDates: string[];
  } | null>(null);
  const [selectableEndDates, setSelectableEndDates] = useState<string[] | undefined>(undefined);

  const [getAvailableEndDates, { loading: endDateLoading }] = useLazyQuery<
    SP_QueryAvaiableEndDateForTenancyOptions,
    SP_QueryAvaiableEndDateForTenancyOptionsVariables
  >(QUERY_AVAIABLE_END_DATE_FOR_TENANCY_OPTIONS, {
    onCompleted: data => {
      setSelectableEndDates(data?.getAvailableEndDatesForTenancyOption ?? []);
    },
    fetchPolicy: 'no-cache',
  });

  const handleFlexibleTenancySelect = useCallback(
    (params: {
      tenancyOptionId: string;
      startDate: string;
      endDate: string;
      selectableStartDates: string[];
    }) => {
      setFlexibleTenancyInfo(params);
      setSelectableEndDates(undefined);
      setTenancyDate({ startDate: undefined, endDate: undefined });
    },
    [setTenancyDate]
  );

  const handleFlexibleStartDateChange = useCallback(
    (date: Date) => {
      setTenancyDate({ startDate: date, endDate: undefined });
      setSelectableEndDates(undefined);
      if (propertyId && flexibleTenancyInfo) {
        getAvailableEndDates({
          variables: {
            input: {
              propertyId,
              tenancyOptionId: flexibleTenancyInfo.tenancyOptionId,
              roomTypeId: roomTypeId || undefined,
              startDate: moment(date).format('YYYY-MM-DD'),
            },
          },
        });
      }
    },
    [propertyId, flexibleTenancyInfo, roomTypeId, getAvailableEndDates, setTenancyDate]
  );

  const roomTypeOptions = useMemo(() => {
    return roomTypes
      ?.map(item => {
        return {
          label: item?.name,
          value: item?.id,
        };
      })
      .sort((current, next) => {
        return current.label?.toLowerCase() > next.label?.toLowerCase() ? 1 : -1;
      });
  }, [roomTypes]);

  const roomTypeSelected = useMemo(() => {
    return roomTypes?.find(item => item?.id === roomTypeId);
  }, [roomTypeId, roomTypes]);

  const initRoomType = roomTypeSelected
    ? {
        label: roomTypeSelected.name,
        value: roomTypeSelected.id,
      }
    : '';

  const formik = useFormik({
    initialValues: { roomTypes: initRoomType },
    onSubmit: () => {},
  });

  return (
    <FormikProvider value={formik}>
      <div className={styles.root}>
        <div className={styles.roomTypeWrapper}>
          <Select
            selectType="custom"
            placeholder={t('select_room_type')}
            options={roomTypeOptions}
            menuShouldScrollIntoView={false}
            label={t('select_bed.room_type.title')}
            name="roomTypes"
            onSelect={(vl: Option) => {
              if (vl) {
                setRoomTypeId(vl.value);
                setSelectTenancyType(null);
                setTenancyOptionLengthType('');
                setFlexibleTenancyInfo(null);
                setSelectableEndDates(undefined);
              }
            }}
          />
          {roomTypeSelected && (
            <div className={styles.result}>
              {roomTypeSelected?.category && (
                <div className={styles.result__item}>
                  <IconRoom width="28" height="28" />
                  <Typography variant="body3" style={{ marginLeft: 4 }}>
                    {t(`select_bed.room.${roomTypeSelected.category.toLowerCase()}`)}
                  </Typography>
                </div>
              )}
              {roomTypeSelected?.bathroomArrangement && (
                <div className={styles.result__item}>
                  <IconBathroom width="28" height="28" />
                  <Typography variant="body3" style={{ marginLeft: 4 }}>
                    {t(
                      `select_bed.bathroom.${roomTypeSelected?.bathroomArrangement?.toLowerCase()}`
                    )}
                  </Typography>
                </div>
              )}
              {roomTypeSelected?.kitchenArrangement && (
                <div className={styles.result__item}>
                  <IconKitchen width="28" height="28" />
                  <Typography variant="body3" style={{ marginLeft: 4 }}>
                    {t(`select_bed.kitchen.${roomTypeSelected?.kitchenArrangement?.toLowerCase()}`)}
                  </Typography>
                </div>
              )}
            </div>
          )}
        </div>
        <div className={styles.tenancyWrapper}>
          <TenancyOptionSelect
            disabled={!roomTypeSelected}
            loading={tenancyOptionLoading}
            tenancyOptionId={tenancyOptionId}
            setSelectTenancyType={value => {
              setSelectTenancyType(value);
            }}
            setTenancyOptionId={(id: string) => {
              setTenancyOptionId(id);
            }}
            setAcademicYearId={(id: string) => {
              setAcademicYearId(id);
            }}
            setTenancyDate={(value: IDate) => {
              setTenancyDate(value);
            }}
            setTenancyOptionRateType={(value: any) => {
              setTenancyOptionRateType(value);
            }}
            setTenancyOptionLengthType={(value: any) => {
              setTenancyOptionLengthType(value);
            }}
            tenancyDate={tenancyDate}
            availableTenancyOptions={availableTenancyOptions}
            onFlexibleTenancySelect={handleFlexibleTenancySelect}
          />

          {selectTenancyType === TenancyTypeOption.FLEXIBLE && flexibleTenancyInfo && (
            <DateRangePicker
              key={flexibleTenancyInfo.tenancyOptionId}
              fromMonth={new Date(flexibleTenancyInfo.startDate)}
              toMonth={new Date(flexibleTenancyInfo.endDate)}
              startDate={tenancyDate.startDate}
              endDate={tenancyDate.endDate}
              selectableStartDates={flexibleTenancyInfo.selectableStartDates}
              selectableEndDates={selectableEndDates}
              endDateLoading={endDateLoading}
              onStartDateChange={handleFlexibleStartDateChange}
              onEndDateChange={date => {
                setTenancyDate({ ...tenancyDate, endDate: date });
              }}
            />
          )}

          {tenancyDate?.endDate &&
            tenancyDate.startDate &&
            selectTenancyType === TenancyTypeOption.FIXED && (
              <div className={styles.result}>
                <div className={styles.result__tenancy}>
                  <IconDatepicker width="15" height="16" fill="var(--color-purple_4)" />
                  <div className={styles.result__tenancyLength}>
                    {tenancyOptionLengthType === TenancyLengthOptions.ROLLING && (
                      <Typography variant="body3">
                        {t('from')} {moment(tenancyDate.startDate).format('ddd, ll')}
                      </Typography>
                    )}

                    {tenancyOptionLengthType !== TenancyLengthOptions.ROLLING && (
                      <>
                        <Typography variant="body3">
                          {moment(tenancyDate.startDate).format('ddd, ll')}
                        </Typography>
                        <IconRight
                          width="12"
                          height="12"
                          fill="var(--color-purple_6)"
                          style={{ margin: '0 8px' }}
                        />
                        <Typography variant="body3">
                          {moment(tenancyDate.endDate).format('ddd, ll')}
                        </Typography>
                      </>
                    )}
                  </div>
                </div>

                {tenancyOptionLengthType === TenancyLengthOptions.ROLLING && (
                  <div className={styles.result__tenancy}>
                    <IconInfo fill="var(--color-purple_4)" width="16" height="16" />
                    <div className={styles.result__tenancyLength}>
                      <Typography variant="body3" color="var(--color-purple_4)">
                        {t('with_no_end_data')}
                      </Typography>
                    </div>
                  </div>
                )}
              </div>
            )}
        </div>
      </div>
    </FormikProvider>
  );
};

export default FilterSelectorForm;
